/**
 * glowSpriteFactory — the ONE way to mint a tinted, additive glow sprite for
 * the ambient/atmosphere effect layers. Consolidates the four hand-rolled
 * copies (AtmosphereLayer.makeGlowSprite + the inline creation in SoftLight /
 * BloomPulse / SmoothParticleEmitter) into a single seam:
 *   createSprite(glow_soft) -> setTint -> setBlendMode (add, or screen for rays).
 *
 * Returns null (never throws) when the glow texture isn't registered yet — a
 * quieter scene during the boot texture race, exactly like the old paths.
 * Pixi stays behind ISprite; the spriteFactory is injectable for headless tests.
 */
import { createSprite } from '../../rendering/utils/Sprite';
import type { ISprite, SpriteOptions, VfxBlendMode } from '../../rendering/IRenderer';
import { Z_LAYERS } from '../../rendering/Z_LAYERS';
import { hasSharedTexture } from '../../rendering/PixiSprite';
import type { GlowTextureKey } from '../../rendering/glowTextures';

export type { GlowTextureKey };

export interface GlowSpriteOpts {
  x: number;
  y: number;
  /** Sprite diameter (px) — width === height. */
  size: number;
  color: number;
  alpha: number;
  /** Glow texture (default 'glow_soft'). */
  textureKey?: GlowTextureKey | string;
  /** Blend mode (default 'add'; 'ray_ribbon' defaults to 'screen'). */
  blendMode?: VfxBlendMode;
  /** Z depth (default Z_LAYERS.ATMOSPHERE). */
  depth?: number;
  /** Optional per-layer display container (Scene FX v3 M1) — forwarded into the
   *  sprite's SpriteOptions.parent so the layer's container owns its alpha. */
  parent?: { addChild(child: unknown): void };
  /** Injectable for tests; defaults to the real createSprite. */
  spriteFactory?: (o: SpriteOptions) => ISprite | null;
}

export function createGlowSprite(o: GlowSpriteOpts): ISprite | null {
  const textureKey = o.textureKey ?? 'glow_soft';
  if (!hasSharedTexture(textureKey)) return null; // texture not ready — quieter scene, never a crash

  const factory = o.spriteFactory ?? ((opts: SpriteOptions) => createSprite(null, opts));
  const sprite = factory({
    x: o.x,
    y: o.y,
    initialTextureKey: textureKey,
    width: o.size,
    height: o.size,
    depth: o.depth ?? Z_LAYERS.ATMOSPHERE,
    alpha: o.alpha,
    parent: o.parent,
  });
  if (!sprite) return null;

  sprite.setTint(o.color);
  sprite.setBlendMode(o.blendMode ?? (textureKey === 'ray_ribbon' ? 'screen' : 'add'));
  return sprite;
}
